// src/utils/auditLog.js
// Utilitaires pour gérer les logs d'audit - NE PAS MODIFIER

/**
 * Types d'actions auditables
 */
export const AUDIT_ACTIONS = {
    // Listings
    LISTING_CREATED: 'listing_created',
    LISTING_UPDATED: 'listing_updated',
    LISTING_DELETED: 'listing_deleted',
    LISTING_STATUS_CHANGED: 'listing_status_changed',
    LISTING_PUBLISHED: 'listing_published',
    LISTING_REJECTED: 'listing_rejected',
    LISTING_DISABLED: 'listing_disabled',
  
    // Réservations
    RESERVATION_CREATED: 'reservation_created',
    RESERVATION_STATUS_CHANGED: 'reservation_status_changed',
    RESERVATION_APPROVED: 'reservation_approved',
    RESERVATION_REJECTED: 'reservation_rejected',
    RESERVATION_DELETED: 'reservation_deleted',
  
    // Utilisateurs
    USER_CREATED: 'user_created',
    USER_UPDATED: 'user_updated',
    USER_DELETED: 'user_deleted',
    USER_BANNED: 'user_banned',
  
    // Administrateurs
    ADMIN_CREATED: 'admin_created',
    ADMIN_UPDATED: 'admin_updated',
    ADMIN_DELETED: 'admin_deleted',
    ADMIN_ROLE_CHANGED: 'admin_role_changed',
  
    // Authentification
    LOGIN: 'login',
    LOGOUT: 'logout',
    PASSWORD_CHANGED: 'password_changed',
  
    // Paramètres
    SETTINGS_UPDATED: 'settings_updated',
    QUARTIER_CREATED: 'quartier_created',
    QUARTIER_UPDATED: 'quartier_updated',
    QUARTIER_DELETED: 'quartier_deleted',
    TYPE_CREATED: 'type_created',
    TYPE_UPDATED: 'type_updated',
    TYPE_DELETED: 'type_deleted',
  };
  
  /**
   * Descriptions lisibles des actions
   */
  export const AUDIT_ACTION_DESCRIPTIONS = {
    listing_created: 'Annonce créée',
    listing_updated: 'Annonce modifiée',
    listing_deleted: 'Annonce supprimée',
    listing_status_changed: 'Statut de l\'annonce modifié',
    listing_published: 'Annonce publiée',
    listing_rejected: 'Annonce rejetée',
    listing_disabled: 'Annonce désactivée',
    reservation_created: 'Réservation créée',
    reservation_status_changed: 'Statut de réservation modifié',
    reservation_approved: 'Réservation approuvée',
    reservation_rejected: 'Réservation rejetée',
    reservation_deleted: 'Réservation supprimée',
    user_created: 'Utilisateur créé',
    user_updated: 'Utilisateur modifié',
    user_deleted: 'Utilisateur supprimé',
    user_banned: 'Utilisateur banni',
    admin_created: 'Administrateur créé',
    admin_updated: 'Administrateur modifié',
    admin_deleted: 'Administrateur supprimé',
    admin_role_changed: 'Rôle administrateur modifié',
    login: 'Connexion',
    logout: 'Déconnexion',
    password_changed: 'Mot de passe modifié',
    settings_updated: 'Paramètres mis à jour',
    quartier_created: 'Quartier créé',
    quartier_updated: 'Quartier modifié',
    quartier_deleted: 'Quartier supprimé',
    type_created: 'Type créé',
    type_updated: 'Type modifié',
    type_deleted: 'Type supprimé',
  };
  
  /**
   * Créer une entrée de log d'audit
   * @param {object} config - Configuration du log
   * @returns {object} Objet log complet
   */
  export const createAuditLog = (config) => {
    const {
      action = '',
      adminId = null,
      adminName = 'Unknown',
      adminRole = 'ADMIN',
      targetType = '', // 'listing', 'reservation', 'user', 'admin', etc.
      targetId = null,
      targetName = '',
      changes = {}, // { field: { before, after } }
      reason = '',
      ipAddress = '',
      userAgent = '',
      status = 'success', // 'success', 'error', 'warning'
      errorMessage = null,
    } = config;
  
    return {
      id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      action,
      actionDescription: AUDIT_ACTION_DESCRIPTIONS[action] || action,
      adminId,
      adminName,
      adminRole,
      targetType,
      targetId,
      targetName,
      changes,
      reason,
      ipAddress,
      userAgent,
      status,
      errorMessage,
      timestamp: new Date().toISOString(),
      date: new Date(),
    };
  };
  
  /**
   * Sauvegarder un log d'audit
   * @param {object} log - Objet log
   * @returns {boolean} true si succès
   */
  export const saveAuditLog = (log) => {
    try {
      // TODO: Envoyer à l'API pour sauvegarder en base de données
      // Pour l'instant, on peut logger en console
      console.log('📋 Audit Log:', log);
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du log d\'audit:', error);
      return false;
    }
  };
  
  /**
   * Logger la création d'une annonce
   * @param {object} adminData - Données de l'admin
   * @param {object} listing - Objet annonce créée
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logListingCreated = (adminData, listing, ipAddress = '') => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.LISTING_CREATED,
      adminId: adminData.id,
      adminName: adminData.name,
      adminRole: adminData.role,
      targetType: 'listing',
      targetId: listing.id,
      targetName: listing.title,
      changes: {
        title: { before: null, after: listing.title },
        category: { before: null, after: listing.category },
        price: { before: null, after: listing.price },
        quartier: { before: null, after: listing.quartier },
      },
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger la modification d'une annonce
   * @param {object} adminData - Données de l'admin
   * @param {object} listing - Annonce modifiée
   * @param {object} changes - Changements { field: { before, after } }
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logListingUpdated = (adminData, listing, changes, ipAddress = '') => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.LISTING_UPDATED,
      adminId: adminData.id,
      adminName: adminData.name,
      adminRole: adminData.role,
      targetType: 'listing',
      targetId: listing.id,
      targetName: listing.title,
      changes,
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger la suppression d'une annonce
   * @param {object} adminData - Données de l'admin
   * @param {object} listing - Annonce supprimée
   * @param {string} reason - Raison de la suppression
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logListingDeleted = (adminData, listing, reason = '', ipAddress = '') => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.LISTING_DELETED,
      adminId: adminData.id,
      adminName: adminData.name,
      adminRole: adminData.role,
      targetType: 'listing',
      targetId: listing.id,
      targetName: listing.title,
      reason,
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger le changement de statut d'une annonce
   * @param {object} adminData - Données de l'admin
   * @param {object} listing - Annonce
   * @param {string} oldStatus - Ancien statut
   * @param {string} newStatus - Nouveau statut
   * @param {string} reason - Raison du changement
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logListingStatusChanged = (
    adminData,
    listing,
    oldStatus,
    newStatus,
    reason = '',
    ipAddress = ''
  ) => {
    const actionMap = {
      published: AUDIT_ACTIONS.LISTING_PUBLISHED,
      rejected: AUDIT_ACTIONS.LISTING_REJECTED,
      disabled: AUDIT_ACTIONS.LISTING_DISABLED,
    };
  
    const log = createAuditLog({
      action: actionMap[newStatus] || AUDIT_ACTIONS.LISTING_STATUS_CHANGED,
      adminId: adminData.id,
      adminName: adminData.name,
      adminRole: adminData.role,
      targetType: 'listing',
      targetId: listing.id,
      targetName: listing.title,
      changes: {
        status: { before: oldStatus, after: newStatus },
      },
      reason,
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger la modification d'une réservation
   * @param {object} adminData - Données de l'admin
   * @param {object} reservation - Réservation
   * @param {string} oldStatus - Ancien statut
   * @param {string} newStatus - Nouveau statut
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logReservationStatusChanged = (
    adminData,
    reservation,
    oldStatus,
    newStatus,
    ipAddress = ''
  ) => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.RESERVATION_STATUS_CHANGED,
      adminId: adminData.id,
      adminName: adminData.name,
      adminRole: adminData.role,
      targetType: 'reservation',
      targetId: reservation.id,
      targetName: `Réservation #${reservation.id}`,
      changes: {
        status: { before: oldStatus, after: newStatus },
      },
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger la création d'un utilisateur
   * @param {object} adminData - Données de l'admin
   * @param {object} user - Utilisateur créé
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logUserCreated = (adminData, user, ipAddress = '') => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.USER_CREATED,
      adminId: adminData.id,
      adminName: adminData.name,
      adminRole: adminData.role,
      targetType: 'user',
      targetId: user.id,
      targetName: user.email,
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger la suppression d'un utilisateur
   * @param {object} adminData - Données de l'admin
   * @param {object} user - Utilisateur supprimé
   * @param {string} reason - Raison de la suppression
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logUserDeleted = (adminData, user, reason = '', ipAddress = '') => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.USER_DELETED,
      adminId: adminData.id,
      adminName: adminData.name,
      adminRole: adminData.role,
      targetType: 'user',
      targetId: user.id,
      targetName: user.email,
      reason,
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger la création d'un administrateur
   * @param {object} creatorData - Données du créateur (SUPER_ADMIN)
   * @param {object} newAdmin - Nouvel administrateur
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logAdminCreated = (creatorData, newAdmin, ipAddress = '') => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.ADMIN_CREATED,
      adminId: creatorData.id,
      adminName: creatorData.name,
      adminRole: creatorData.role,
      targetType: 'admin',
      targetId: newAdmin.id,
      targetName: newAdmin.email,
      changes: {
        role: { before: null, after: newAdmin.role },
      },
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger la modification du rôle d'un administrateur
   * @param {object} adminData - Données de l'admin qui change le rôle
   * @param {object} targetAdmin - Admin dont le rôle est changé
   * @param {string} oldRole - Ancien rôle
   * @param {string} newRole - Nouveau rôle
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logAdminRoleChanged = (
    adminData,
    targetAdmin,
    oldRole,
    newRole,
    ipAddress = ''
  ) => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.ADMIN_ROLE_CHANGED,
      adminId: adminData.id,
      adminName: adminData.name,
      adminRole: adminData.role,
      targetType: 'admin',
      targetId: targetAdmin.id,
      targetName: targetAdmin.email,
      changes: {
        role: { before: oldRole, after: newRole },
      },
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger la suppression d'un administrateur
   * @param {object} adminData - Données de l'admin qui supprime
   * @param {object} targetAdmin - Admin supprimé
   * @param {string} reason - Raison de la suppression
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logAdminDeleted = (adminData, targetAdmin, reason = '', ipAddress = '') => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.ADMIN_DELETED,
      adminId: adminData.id,
      adminName: adminData.name,
      adminRole: adminData.role,
      targetType: 'admin',
      targetId: targetAdmin.id,
      targetName: targetAdmin.email,
      reason,
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger une connexion
   * @param {object} adminData - Données de l'admin
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logLogin = (adminData, ipAddress = '') => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.LOGIN,
      adminId: adminData.id,
      adminName: adminData.email,
      adminRole: adminData.role,
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger une déconnexion
   * @param {object} adminData - Données de l'admin
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logLogout = (adminData, ipAddress = '') => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.LOGOUT,
      adminId: adminData.id,
      adminName: adminData.email,
      adminRole: adminData.role,
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger un changement de mot de passe
   * @param {object} adminData - Données de l'admin
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logPasswordChanged = (adminData, ipAddress = '') => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.PASSWORD_CHANGED,
      adminId: adminData.id,
      adminName: adminData.email,
      adminRole: adminData.role,
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger la modification des paramètres
   * @param {object} adminData - Données de l'admin
   * @param {object} changes - Changements apportés
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logSettingsUpdated = (adminData, changes, ipAddress = '') => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.SETTINGS_UPDATED,
      adminId: adminData.id,
      adminName: adminData.name,
      adminRole: adminData.role,
      changes,
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger la création d'un quartier
   * @param {object} adminData - Données de l'admin
   * @param {object} quartier - Quartier créé
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logQuartierCreated = (adminData, quartier, ipAddress = '') => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.QUARTIER_CREATED,
      adminId: adminData.id,
      adminName: adminData.name,
      adminRole: adminData.role,
      targetType: 'quartier',
      targetId: quartier.id,
      targetName: quartier.name,
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Logger la création d'un type
   * @param {object} adminData - Données de l'admin
   * @param {object} type - Type créé
   * @param {string} ipAddress - Adresse IP
   * @returns {object} Log créé
   */
  export const logTypeCreated = (adminData, type, ipAddress = '') => {
    const log = createAuditLog({
      action: AUDIT_ACTIONS.TYPE_CREATED,
      adminId: adminData.id,
      adminName: adminData.name,
      adminRole: adminData.role,
      targetType: 'type',
      targetId: type.id,
      targetName: type.name,
      ipAddress,
    });
  
    saveAuditLog(log);
    return log;
  };
  
  /**
   * Obtenir l'adresse IP du client (en frontend, ce sera une valeur par défaut)
   * @returns {string} Adresse IP (ou valeur par défaut)
   */
  export const getClientIpAddress = () => {
    // Dans un vrai app, cela viendrait du backend
    return 'client-ip';
  };
  
  /**
   * Obtenir le User-Agent du navigateur
   * @returns {string} User-Agent
   */
  export const getUserAgent = () => {
    return navigator.userAgent || 'unknown';
  };
  
  /**
   * Formater un log d'audit pour l'affichage
   * @param {object} log - Objet log
   * @param {string} language - 'fr' ou 'en'
   * @returns {string} Description formatée
   */
  export const formatAuditLog = (log, language = 'fr') => {
    const descriptions = {
      fr: {
        summary: `${log.adminName} a ${log.actionDescription?.toLowerCase()} ${log.targetName || log.targetType}`,
        timestamp: new Date(log.timestamp).toLocaleString('fr-FR'),
      },
      en: {
        summary: `${log.adminName} ${log.actionDescription?.toLowerCase()} ${log.targetName || log.targetType}`,
        timestamp: new Date(log.timestamp).toLocaleString('en-US'),
      },
    };
  
    return descriptions[language] || descriptions.fr;
  };
  
  /**
   * Générer un rapport d'audit
   * @param {Array} logs - Tableau de logs
   * @param {object} options - Options de filtrage
   * @returns {object} Rapport généré
   */
  export const generateAuditReport = (logs = [], options = {}) => {
    const {
      action = null,
      adminId = null,
      targetType = null,
      startDate = null,
      endDate = null,
    } = options;
  
    let filtered = logs;
  
    // Filtrer par action
    if (action) {
      filtered = filtered.filter((log) => log.action === action);
    }
  
    // Filtrer par admin
    if (adminId) {
      filtered = filtered.filter((log) => log.adminId === adminId);
    }
  
    // Filtrer par type de cible
    if (targetType) {
      filtered = filtered.filter((log) => log.targetType === targetType);
    }
  
    // Filtrer par plage de dates
    if (startDate) {
      filtered = filtered.filter((log) => new Date(log.timestamp) >= new Date(startDate));
    }
    if (endDate) {
      filtered = filtered.filter((log) => new Date(log.timestamp) <= new Date(endDate));
    }
  
    // Compter les actions
    const actionCounts = {};
    filtered.forEach((log) => {
      actionCounts[log.action] = (actionCounts[log.action] || 0) + 1;
    });
  
    // Compter par admin
    const adminCounts = {};
    filtered.forEach((log) => {
      const key = `${log.adminName} (${log.adminRole})`;
      adminCounts[key] = (adminCounts[key] || 0) + 1;
    });
  
    return {
      totalLogs: filtered.length,
      filteredLogs: filtered,
      actionCounts,
      adminCounts,
      generatedAt: new Date().toISOString(),
    };
  };